import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { UserPlus, KeyRound, AlertTriangle, Briefcase } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "candidate",
    invite_code: "",
    company_name: "", job_title: "", phone: "", linkedin_url: "", company_website: "", org_type: "business",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pre-fill `invite_code` (and optionally `role`) from the URL so links like
  // `/register?invite=ACME-OPS&role=recruiter` open the right form pre-stamped.
  useEffect(() => {
    const inv = (searchParams.get("invite") || searchParams.get("invite_code") || "").trim().toUpperCase();
    const r = (searchParams.get("role") || "").trim().toLowerCase();
    if (inv || (r && (r === "candidate" || r === "recruiter"))) {
      setForm((f) => ({
        ...f,
        invite_code: inv || f.invite_code,
        role: (r === "candidate" || r === "recruiter") ? r : f.role,
      }));
    }
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(form);
      navigate(u.role === "candidate" ? "/candidate" : "/recruiter", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const googleSignup = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data: cfg } = await (await import("../lib/api")).api.get("/auth/google/config");
      if (!cfg.enabled) {
        setError("Google sign-in is not configured. Contact admin.");
        return;
      }
      // Persist signup intent via BOTH localStorage AND OAuth `state` param so it survives
      // browsers that drop localStorage across a cross-origin OAuth roundtrip (Safari/private/iframe).
      localStorage.setItem("pending_role", form.role);
      if (form.invite_code) localStorage.setItem("pending_invite", form.invite_code);
      const statePayload = btoa(JSON.stringify({
        mode: "register",
        role: form.role,
        invite_code: form.invite_code || "",
      })).replace(/=+$/, "");
      const redirectUri = window.location.origin + "/auth/google";
      const params = new URLSearchParams({
        client_id: cfg.client_id,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        access_type: "online",
        include_granted_scopes: "true",
        prompt: "select_account",
        state: statePayload,
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      setError("Could not start Google sign-in.");
    }
  };

  const inputCls = "w-full border border-[#0F0F0F] px-4 py-3 rounded-sm text-base bg-transparent";

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-12">
      <div className="col-span-12 lg:col-span-7 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="label-eyebrow">Sign up</div>
          <h1 className="font-display font-black text-5xl tracking-tight mt-3">Create yours.</h1>
          <p className="text-[#5C5C5C] mt-3">Choose your side of the table.</p>

          <div className="mt-8 grid grid-cols-2 gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {[
              { v: "candidate", t: "I'm looking" },
              { v: "recruiter", t: "I'm hiring" },
            ].map(({ v, t }) => (
              <button
                key={v}
                type="button"
                data-testid={`role-${v}`}
                onClick={() => set("role", v)}
                className={`p-4 text-left ${form.role === v ? "bg-[#0F0F0F] text-[#FDFCF8]" : "bg-[#FDFCF8] hover:bg-[#F3F2EB]"}`}
              >
                <div className="label-eyebrow" style={{ color: form.role === v ? "#D94833" : undefined }}>{t}</div>
                <div className="font-display font-bold text-lg mt-1 capitalize">{v}</div>
              </button>
            ))}
          </div>

          {form.role === "recruiter" && (
            <div className="mt-5 border border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5 p-3 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[var(--brand-primary)] flex-shrink-0 mt-0.5" />
              <span>Recruiter accounts require <strong>Owner approval</strong> before you can post roles.</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Full name</label>
              <input data-testid="reg-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                className={inputCls} placeholder="Jordan Reyes" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input data-testid="reg-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                className={inputCls} placeholder="you@company.com" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)}
                className={inputCls} placeholder="At least 6 characters" />
            </div>

            {(form.role === "candidate" || form.role === "recruiter") && (
              <div>
                <label className="label-eyebrow block mb-2 flex items-center gap-1.5">
                  <KeyRound className="w-3 h-3" /> Invite code <span className="text-[#5C5C5C] normal-case tracking-normal lowercase">{form.role === "recruiter" ? "(if you were invited to a team)" : "(optional)"}</span>
                </label>
                <input data-testid="reg-invite" value={form.invite_code} onChange={(e) => set("invite_code", e.target.value.toUpperCase())}
                  className={`${inputCls} uppercase tracking-widest`} placeholder="Got a code? Paste it here" />
                {form.role === "candidate" && (
                  <p className="text-xs text-[#5C5C5C] mt-1.5">Skip this if you don&apos;t have one — candidate signup is free for everyone.</p>
                )}
                {form.role === "recruiter" && form.invite_code && (
                  <p data-testid="reg-invite-recruiter-hint" className="text-xs text-[var(--brand-primary)] mt-1.5">Joining an existing team — company name + business info below will be filled in from your invite.</p>
                )}
              </div>
            )}

            {form.role === "recruiter" && (
              <>
                <div className="pt-2 border-t border-[#E2E0D8]">
                  <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Briefcase className="w-3 h-3" /> Business information {form.invite_code && <span className="text-[#5C5C5C] normal-case tracking-normal lowercase">(optional with an invite)</span>}</div>
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Company name {!form.invite_code && "*"}</label>
                  <input data-testid="reg-company" required={!form.invite_code} value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                    className={inputCls} placeholder={form.invite_code ? "Joined via invite — leave blank" : "Acme Marine Ltd, Foundry Press, etc."} />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Your job title {!form.invite_code && "*"}</label>
                  <input data-testid="reg-jobtitle" required={!form.invite_code} value={form.job_title} onChange={(e) => set("job_title", e.target.value)}
                    className={inputCls} placeholder="Head of Talent, Recruiter…" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Company website</label>
                  <input data-testid="reg-website" type="url" value={form.company_website} onChange={(e) => set("company_website", e.target.value)}
                    className={inputCls} placeholder="https://acme.com" />
                </div>
                <div>
                  <div className="label-eyebrow block mb-2">Organisation type *</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "business", t: "Real business", h: "Standard hiring on the platform." },
                      { v: "game", t: "Game group", h: "Online community / game — a disclaimer is shown on every listing." },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        data-testid={`reg-orgtype-${opt.v}`}
                        onClick={() => set("org_type", opt.v)}
                        className={`text-sm border px-3 py-2.5 text-left transition ${form.org_type === opt.v ? "border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFCF8]" : "border-[#E2E0D8] hover:border-[#0F0F0F]"}`}
                      >
                        <div className="font-semibold">{opt.t}</div>
                        <div className={`text-[10px] mt-0.5 ${form.org_type === opt.v ? "text-[#FDFCF8]/70" : "text-[#5C5C5C]"}`}>{opt.h}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-eyebrow block mb-2">Phone</label>
                    <input data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                      className={inputCls} placeholder="+44…" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">LinkedIn</label>
                    <input data-testid="reg-linkedin" type="url" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)}
                      className={inputCls} placeholder="linkedin.com/in/…" />
                  </div>
                </div>
                <p className="text-xs text-[#5C5C5C]">
                  Starts on the <strong>Free</strong> plan — 2 active roles, 100 applicants/month. <Link to="/pricing" className="underline">See plans</Link>.
                </p>
              </>
            )}

            {error && <div data-testid="reg-error" className="text-sm text-[var(--brand-primary)] font-medium">{error}</div>}
            <button data-testid="reg-submit" disabled={loading} type="submit" className="btn-terra w-full text-base">
              {loading ? "Creating…" : (<><UserPlus className="w-4 h-4" /> Create account</>)}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E2E0D8]" />
            <span className="label-eyebrow">or</span>
            <div className="flex-1 h-px bg-[#E2E0D8]" />
          </div>

          <button data-testid="reg-google" onClick={googleSignup}
            className="w-full border border-[#0F0F0F] py-3 rounded-sm font-semibold hover:bg-[#0F0F0F] hover:text-[#FDFCF8] transition">
            Continue with Google as {form.role}
          </button>

          <p className="mt-8 text-sm text-[#5C5C5C]">
            Already have an account? <Link to="/login" className="font-semibold underline underline-offset-4">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block lg:col-span-5 bg-[#F3F2EB] relative border-l border-[#E2E0D8]">
        <div className="absolute inset-0" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1560264280-88b68371db39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)", backgroundSize: "cover", opacity: 0.6 }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FDFCF8] via-[#FDFCF8]/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="label-eyebrow">Free for candidates today</div>
          <h2 className="font-display font-black text-4xl mt-3 leading-tight">
            Hire deliberately. <br />Apply without barriers.
          </h2>
        </div>
      </div>
    </div>
  );
}
